/**
 * Notes you can correct - the pure rules and words (docs/plans/crm-report-builder-search-collab-plan.md
 * A1.2 to A1.4, B1.2 and B1.4). No api import chain, so components and tests use it without the
 * network layer.
 *
 * noteKind and noteActions are the DISPLAY mirror of the server's rule: the SQL functions
 * crm.edit_note / crm.delete_note are the authority, and crmNotes.js refuses early with the same
 * truth table. Where the SQL and the service's JS read a flag differently, this module takes the
 * stricter reading, so the menu never offers a change the server would refuse:
 *   - `typed` needs metadata.manual === true (the service's reading) and NO `event` key at all (the
 *     SQL's `metadata ? 'event'`);
 *   - the 24-hour window is measured from created_at, the column the SQL reads, and is open while
 *     now - created_at < 24 h (the SQL refuses at created_at <= now() - 24 hours). A row without
 *     created_at (an api that predates Part 1) gets no author actions: fail closed.
 *
 * Every recruiter-facing word about a changed note is rendered here: "edited 2 min ago", "Note
 * deleted by Dana Smith, Sep 26 3:14 PM: Duplicate", "Dana Smith edited this note just now", the
 * reasons and the history rows.
 */
import type { Communication } from '../types';
import type {
  NoteActions,
  NoteConflict,
  NoteHistoryResponse,
  NoteKind,
  NoteReasonCode,
  NoteRevision,
} from './types';

// ---------------------------------------------------------------------------------------------
// The rules' constants (the backend's crmNotes.js carries the same values)
// ---------------------------------------------------------------------------------------------

/** How long the author may edit or delete a typed note (decisions N1 a and N2 a). */
export const NOTE_CHANGE_WINDOW_HOURS = 24;
const NOTE_CHANGE_WINDOW_MS = NOTE_CHANGE_WINDOW_HOURS * 3600e3;

/** A note's longest text (crmMessaging MAX_NOTE_BODY; crmNotes NOTE_BODY_MAX). */
export const NOTE_BODY_MAX = 5000;

/** The longest reason sentence (crmNotes NOTE_REASON_TEXT_MAX; the history table's CHECK). */
export const NOTE_REASON_TEXT_MAX = 500;

export interface NoteReasonOption {
  code: NoteReasonCode;
  label: string;
  /** Only Other needs a sentence. */
  needsText: boolean;
}

/** The reason pick-list, in the order it is offered (plan A1.2; decision N5 a). */
export const NOTE_REASONS: ReadonlyArray<NoteReasonOption> = [
  { code: 'typo', label: 'Typo or wording', needsText: false },
  { code: 'wrong_info', label: 'Wrong information', needsText: false },
  { code: 'wrong_patient', label: 'Wrong patient', needsText: false },
  { code: 'duplicate', label: 'Duplicate', needsText: false },
  { code: 'other', label: 'Other', needsText: true },
];

// ---------------------------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------------------------

/** "Now" as a Date or epoch milliseconds; the current time when absent. */
export type NoteNow = Date | number;

const nowMs = (now: NoteNow | undefined): number =>
  now === undefined ? Date.now() : typeof now === 'number' ? now : now.getTime();

const timeOf = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
};

const metadataOf = (c: { metadata?: unknown } | null | undefined): Record<string, unknown> => {
  const m = c?.metadata;
  return m && typeof m === 'object' && !Array.isArray(m) ? (m as Record<string, unknown>) : {};
};

const editCountOf = (c: { edit_count?: number } | null | undefined): number =>
  typeof c?.edit_count === 'number' && Number.isFinite(c.edit_count) ? c.edit_count : 0;

/** ICU may put a narrow no-break space before AM/PM; the words use a plain space. */
const plainSpaces = (s: string): string => s.replace(/[\u00a0\u202f]/g, ' ');

/** The server's name, else the team list's, else the fallback. */
function nameFor(
  userId: string | null | undefined,
  serverName: string | null | undefined,
  nameOf: NoteWordsOptions['nameOf'],
  fallback: string,
): string {
  const given = typeof serverName === 'string' ? serverName.trim() : '';
  if (given) return given;
  if (userId && nameOf) {
    const found = nameOf(userId);
    if (typeof found === 'string' && found.trim()) return found.trim();
  }
  return fallback;
}

/** "Sep 26", or "Sep 26, 2025" outside the current year. */
function dayWords(t: number, now: number): string {
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return plainSpaces(d.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' }));
}

/** "just now", "2 min ago", "3 h ago", "2 d ago", then the day ("Sep 26"). Null when unknown. */
function agoWords(iso: string | null | undefined, now: number): string | null {
  const t = timeOf(iso);
  if (t === null) return null;
  const diff = now - t;
  if (diff < 60e3) return 'just now';
  const minutes = Math.floor(diff / 60e3);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(diff / 3600e3);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(diff / 86400e3);
  if (days < 7) return `${days} d ago`;
  return dayWords(t, now);
}

/** A moment, in the marker's and the history's words: "Sep 26 3:14 PM" ('' when unknown). */
export function noteWhenWords(iso: string | null | undefined, now?: NoteNow): string {
  const t = timeOf(iso);
  if (t === null) return '';
  const time = plainSpaces(new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }));
  return `${dayWords(t, nowMs(now))} ${time}`;
}

// ---------------------------------------------------------------------------------------------
// The rule (B1.2): which notes a person typed, and what the viewer may do with one
// ---------------------------------------------------------------------------------------------

/**
 * Who wrote a note: 'typed' (channel note, metadata.manual === true, no `event` key), 'recruit_mirror'
 * (metadata.event 'recruit_note'), else 'system'. A row on any other channel is 'system'.
 */
export function noteKind(c: Pick<Communication, 'channel' | 'metadata'> | null | undefined): NoteKind {
  if (!c || c.channel !== 'note') return 'system';
  const meta = metadataOf(c);
  if (meta.event === 'recruit_note') return 'recruit_mirror';
  if (Object.prototype.hasOwnProperty.call(meta, 'event')) return 'system';
  return meta.manual === true ? 'typed' : 'system';
}

/** A deleted note keeps its row with deleted_at set (body ''); render the marker, not the text. */
export function isDeletedNote(c: Pick<Communication, 'deleted_at'> | null | undefined): boolean {
  return Boolean(c && c.deleted_at);
}

/** The viewer: their user id, the display-only admin flag (useIsCRMAdmin), and "now". */
export interface NoteViewer {
  userId: string | null | undefined;
  isAdmin: boolean;
  now?: NoteNow;
}

export type NoteActionsInput = Pick<
  Communication,
  'channel' | 'metadata' | 'user_id' | 'created_at' | 'edit_count' | 'deleted_at'
>;

/**
 * The B1.2 truth table, the one the SQL enforces:
 *   edit    a live typed note, by its author, inside 24 hours of created_at (nobody edits another
 *           person's words, admins included);
 *   delete  a live typed note, by its author inside 24 hours, or by a CRM admin at any time;
 *   history a live typed note that has been edited (edit_count > 0), for anyone;
 *   deleted words  a deleted note, for a CRM admin only.
 * System notes and recruitment-list mirrors get nothing.
 */
export function noteActions(c: NoteActionsInput, viewer: NoteViewer): NoteActions {
  const typed = noteKind(c) === 'typed';
  const deleted = isDeletedNote(c);
  const live = typed && !deleted;
  const isAuthor = Boolean(viewer.userId) && c.user_id === viewer.userId;
  const created = timeOf(c.created_at);
  const inWindow = created !== null && nowMs(viewer.now) - created < NOTE_CHANGE_WINDOW_MS;
  const isAdmin = viewer.isAdmin === true;
  return {
    canEdit: live && isAuthor && inWindow,
    canDelete: live && (isAdmin || (isAuthor && inWindow)),
    canViewHistory: live && editCountOf(c) > 0,
    canViewDeletedWords: deleted && isAdmin,
  };
}

// ---------------------------------------------------------------------------------------------
// The words (plan A1.2, A1.3 and A1.4)
// ---------------------------------------------------------------------------------------------

export interface NoteWordsOptions {
  /** "Now"; the current time when absent. Injected in tests. */
  now?: NoteNow;
  /** A user id to a display name (useTeam().byId); a name the server sent wins. */
  nameOf?: (userId: string) => string | null | undefined;
}

/**
 * A reason in words: the label ("Duplicate"), the Other sentence itself, or "Wrong information: the
 * sentence" when another reason carries one. '' when there is nothing to say.
 */
export function noteReasonWords(code: string | null | undefined, text?: string | null): string {
  const sentence = typeof text === 'string' ? text.trim() : '';
  if (code === 'other') return sentence || 'Other';
  const option = NOTE_REASONS.find((r) => r.code === code);
  if (!option) return sentence;
  return sentence ? `${option.label}: ${sentence}` : option.label;
}

/**
 * The card's edited line: "edited 2 min ago" (the author's own edit; the card already names the
 * author), "edited by Dana Smith 2 min ago" when someone else edited it. Null for a note that was
 * never edited, and for a deleted note (it shows the marker instead). Only edit_count / edited_at
 * mean edited: updated_at moves on system refreshes and merges.
 */
export function editedWords(
  c: Pick<Communication, 'user_id' | 'edited_at' | 'edited_by' | 'edit_count' | 'deleted_at'>
    & { edited_by_name?: string | null },
  opts: NoteWordsOptions = {},
): string | null {
  if (!c || isDeletedNote(c)) return null;
  if (editCountOf(c) <= 0 && !c.edited_at) return null;
  const editor = c.edited_by || null;
  const who = editor && editor !== c.user_id
    ? ` by ${nameFor(editor, c.edited_by_name, opts.nameOf, 'a teammate')}`
    : '';
  const ago = agoWords(c.edited_at, nowMs(opts.now));
  return `edited${who}${ago ? ` ${ago}` : ''}`;
}

export interface DeletedMarkerOptions extends NoteWordsOptions {
  /**
   * Why it was deleted, when the caller knows it: its own delete, or the history's deleted row. The
   * timeline row carries no reason, so without this the marker ends at the time.
   */
  reason?: { reason_code: NoteReasonCode | string; reason_text?: string | null } | null;
}

/** The grey marker: "Note deleted by Dana Smith, Sep 26 3:14 PM: Duplicate" (decision N3 a). */
export function deletedMarkerWords(
  c: Pick<Communication, 'deleted_at' | 'deleted_by'> & { deleted_by_name?: string | null },
  opts: DeletedMarkerOptions = {},
): string {
  const who = nameFor(c?.deleted_by, c?.deleted_by_name, opts.nameOf, 'a teammate');
  const when = noteWhenWords(c?.deleted_at, opts.now);
  const why = opts.reason ? noteReasonWords(opts.reason.reason_code, opts.reason.reason_text) : '';
  return `Note deleted by ${who}${when ? `, ${when}` : ''}${why ? `: ${why}` : ''}`;
}

/**
 * The 409 line above Keep theirs / Save mine: "Dana Smith edited this note just now" (plan A1.4, the
 * recruitment list's rule).
 */
export function conflictWords(
  conflict: Pick<NoteConflict, 'current'> | null | undefined,
  opts: NoteWordsOptions = {},
): string {
  const current = conflict?.current;
  const who = nameFor(current?.edited_by, current?.edited_by_name, opts.nameOf, 'A teammate');
  const ago = agoWords(current?.edited_at, nowMs(opts.now));
  return `${who} edited this note${ago ? ` ${ago}` : ''}`;
}

// ---------------------------------------------------------------------------------------------
// The history window (plan A1.3 step 2; decision N4 a)
// ---------------------------------------------------------------------------------------------

/** One row of the history window, oldest first. */
export interface NoteHistoryRow {
  /** Stable React key: 'original', then 'rev-<revision_no>'. */
  key: string;
  /** 'original' = the text as first written; then one row per change. */
  kind: 'original' | 'edited' | 'deleted';
  /** 0 for the original, else the revision's number. */
  revision_no: number;
  /** When (ISO), and in words ("Sep 26 3:14 PM"; '' when unknown). */
  at: string | null;
  when: string;
  /** Who wrote it (the original) or made the change. */
  by: string;
  by_user_id: string | null;
  /** The change was made as a CRM admin (an admin deleting someone else's note). */
  as_admin: boolean;
  /** Why (changes only): the code, and its words ("Duplicate", or the Other sentence). */
  reason_code: NoteReasonCode | null;
  reason: string | null;
  /** The text before the change: null for the original, and when the words are hidden. */
  before: string | null;
  /** The text after: the original's text, an edit's new text; null for a delete and when hidden. */
  after: string | null;
  /** The words are withheld (a deleted note seen by a non-admin). */
  hidden: boolean;
}

/**
 * The history window's rows, oldest first: the original (who wrote it, when, the text as first
 * written, which is revision 1's body_before, or the note's body when it never changed), then each
 * change with who, when, why and the text before and after (a delete has no after). Revisions are
 * put in revision order whatever order they arrive in. `words_hidden` blanks every text.
 */
export function historyRows(
  history: Pick<NoteHistoryResponse, 'note' | 'revisions' | 'words_hidden'> | null | undefined,
  opts: NoteWordsOptions = {},
): NoteHistoryRow[] {
  if (!history) return [];
  const hidden = history.words_hidden === true;
  const note = history.note || null;
  const revisions: NoteRevision[] = (Array.isArray(history.revisions) ? history.revisions : [])
    .filter((r): r is NoteRevision => Boolean(r) && typeof r === 'object')
    .slice()
    .sort((a, b) => (Number(a.revision_no) || 0) - (Number(b.revision_no) || 0));
  const first = revisions[0];
  const rows: NoteHistoryRow[] = [];

  if (note || first) {
    const at = note ? note.created_at || note.occurred_at || null : null;
    let text: string | null = null;
    if (!hidden) text = first ? first.body_before ?? null : note?.body ?? null;
    rows.push({
      key: 'original',
      kind: 'original',
      revision_no: 0,
      at,
      when: noteWhenWords(at, opts.now),
      by: nameFor(note?.user_id, note?.author_name, opts.nameOf, 'a teammate'),
      by_user_id: note?.user_id ?? null,
      as_admin: false,
      reason_code: null,
      reason: null,
      before: null,
      after: text,
      hidden,
    });
  }

  for (const r of revisions) {
    const deleted = r.action === 'deleted';
    rows.push({
      key: `rev-${r.revision_no}`,
      kind: deleted ? 'deleted' : 'edited',
      revision_no: r.revision_no,
      at: r.created_at || null,
      when: noteWhenWords(r.created_at, opts.now),
      by: nameFor(r.actor_user_id, r.actor_name, opts.nameOf, 'a teammate'),
      by_user_id: r.actor_user_id || null,
      as_admin: r.actor_is_admin === true,
      reason_code: r.reason_code || null,
      reason: noteReasonWords(r.reason_code, r.reason_text) || null,
      before: hidden ? null : r.body_before ?? null,
      after: hidden || deleted ? null : r.body_after ?? null,
      hidden,
    });
  }
  return rows;
}

// ---------------------------------------------------------------------------------------------
// The thread after a change
// ---------------------------------------------------------------------------------------------

/**
 * Patch one row of a loaded list in place from a change's answer (the note as it is now), by id:
 * the answer's fields win and fields it leaves out are kept. Every other row keeps its identity, and
 * a list without that id (or no note) comes back as the same array, so React skips the render.
 */
export function patchCommunication<T extends { id: string }>(
  list: T[],
  note: (Partial<T> & { id: string }) | null | undefined,
): T[] {
  if (!note || typeof note.id !== 'string' || !note.id) return list;
  let hit = false;
  const next = list.map((row) => {
    if (row.id !== note.id) return row;
    hit = true;
    return { ...row, ...note };
  });
  return hit ? next : list;
}
